import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  checkStatusCodeSuccess,
  currentDate,
  DownloadLabel,
  errorHandler,
  NormalDate,
  notFound,
  pdfDownload,
  rupeeSymbol,
  TableErrorMessage,
  TaxType,
  validateTable,
  mapToDropdownOptions,
} from "Components/constants/Common";
import { toast } from "react-toastify";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import BaseButton from "Components/Base/BaseButton";
import { QuotationKey, QuotationLabel } from "Components/constants/Quotation";
import * as yup from "yup";
import FormFields from "./FormFields";
import {
  ActionLabel,
  cancelLabel,
  NoLabel,
  submitLabel,
  updateLabel,
  YesLabel,
} from "Components/constants/Role";
import {
  amountCountApi,
  gstCountApi,
  hsnDropdownApi,
  productListApi,
  productRateApi,
  quotationApi,
  quotationPdfApi,
  totalAmountApi,
  viewQuotationApi,
} from "Api/Quotation";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ColDef } from "ag-grid-community";
import Loader from "Components/Base/BaseLoader";
import BaseModal from "Components/Base/BaseModal";
import {
  addHSNLabel,
  Code,
  Hsn,
  Sac,
  Type,
  typeList,
} from "Components/constants/HsnSac";
import BaseInput from "Components/Base/BaseInput";
import { BaseSelect } from "Components/Base/BaseSelect";
import { createHSN } from "Api/HsnSac";
import { UomValueOptions } from "Components/constants/Product";
import { getCustomerList, listOfUomForDropdown } from "Api/common";
import ProductModal from "./ProductModal";
import CustomerModal from "./CustomerModal";
import { calculateServiceGstApi, listOfService } from "Api/service";
import ServiceModal from "./ServiceModal";

const validationSchema = yup.object({
  vendorName: yup
    .string()
    .required(validationMessages.required(QuotationLabel.VendorName)),
  vendorId: yup
    .string()
    .required(validationMessages.required(QuotationLabel.VendorId)),
  consignee_address: yup
    .string()
    .required(validationMessages.required(QuotationLabel.Consignee)),
  buyer_address: yup
    .string()
    .required(validationMessages.required(QuotationLabel.Buyer)),
  dated: yup
    .string()
    .required(validationMessages.required(QuotationLabel.Dated)),
  discount: yup.number().nullable().positive(),
});

const QuotationForm: React.FC = () => {
  document.title = QuotationLabel.QuotationTitle;
  const [vendorOption, setVendorOption] = useState<Array<any>>([]);
  const [vendorData, setVendorData] = useState<Array<any>>([]);
  const [serviceOption, setServiceOption] = useState<Array<any>>([]);
  const [serviceData, setServiceData] = useState<Array<any>>([]);
  const [hsnSacOption, setHsnSacOption] = useState<Array<any>>([]);
  const [vendorId, setVendorId] = useState<any>(null);
  const [productOption, setProductOption] = useState<Array<any>>([]);
  const [productData, setProductData] = useState<Array<any>>([]);
  const [quotationData, setQuotationData] = useState<any>(null);
  const [selectedProducts, setSelectedProducts] = useState<Array<any>>([]);
  const [productValid, setProductValid] = useState<boolean>(true);
  const [totalAmount, setTotalAmount] = useState<any>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [hsnModal, setHsnModal] = useState<boolean>(false);
  const [modalLoader, setModalLoader] = useState<boolean>(false);
  const [vendor, setVendor] = useState<boolean>(false);
  const [selectedVendorData, setSelectedVendorData] = useState<any>(null);
  const [pdfLoader, setPdfLoader] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<boolean>(true);
  const [productModal, setProductModal] = useState<boolean>(false);
  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [serviceModal, setServiceModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { quotationId } = useParams();
  const viewQuotationMode = location?.state?.viewQuotation;
  const gridRef = useRef<AgGridReact<any>>(null);
  const [selectedGst, setSelectedGst] = useState<any>(null);
  const [uomList, setUomList] = useState<any[]>([]);

  const fetchVendor = (callback?: (data: any[]) => void) => {
    setLoader(true);
    const payload = {};
    getCustomerList(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const customerList = res?.data?.map((item: any) => ({
            value: item?.id,
            label: item?.name,
            name: item?.name,
            vendor_id: item?.user_draft_id,
            id: item?.id,
          }));
          customerList.push({
            value: QuotationLabel.AddCustomer,
            label: QuotationLabel.AddCustomer,
            id: QuotationLabel.AddCustomer,
            name: QuotationLabel.AddCustomer,
            vendor_id: QuotationLabel.AddCustomer,
          });
          setVendorOption(customerList);
          setVendorData(res?.data);
          if (callback) {
            callback(res.data);
          }
        } else {
          setVendorData([]);
          setVendorOption([]);
        }
      })
      .catch((err) => {
        setVendorData([]);
        setVendorOption([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const fetchService = (callback?: (data: any[]) => void) => {
    setLoader(true);
    listOfService({})
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const serviceList = res?.data?.services?.map((item: any) => ({
            value: item?.id,
            label: item?.service_name,
            service_name: item?.service_name,
            description: item?.description,
            gst: item?.gst,
            id: item?.id,
            withGstAmount: item?.with_gst_amount,
            cost: item?.cost,
            gstAmount: item?.gst_amount,
            hsnSacCode: item?.hsnSac?.code,
          }));
          serviceList.push({
            value: QuotationLabel.AddService,
            label: QuotationLabel.AddService,
            id: QuotationLabel.AddService,
            service_name: QuotationLabel.AddService,
          });
          setServiceOption(serviceList);
          setServiceData(serviceList);
          if (callback) {
            callback(res.data);
          }
        } else {
          setServiceOption([]);
        }
      })
      .catch((err) => {
        setServiceOption([]);
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (quotationId) {
      setVendor(true);
    } else {
      setVendor(false);
    }
    setInitialValues(true);
  }, []);

  const countAmount = (data: any) => {
    const payload = {
      discount_value:
        formik.values.discount === "" ? null : formik.values.discount,
      quantity: data.quantity,
      gst: data.gst,
      rate: data.rate,
    };
    const isValidPayload =
      payload.quantity !== null &&
      payload.gst !== null &&
      payload.rate !== null;
    if (isValidPayload) {
      setLoader(true);
      amountCountApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const updatedAmount = res?.data;
            setSelectedProducts((prevProducts) =>
              prevProducts.map((product) =>
                product.product_variant_id === data.product_variant_id
                  ? { ...product, amount: updatedAmount }
                  : product
              )
            );
          }
        })
        .catch((err) => {
          return err;
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };
  const countGstAmount = (data: any) => {
    const payload = {
      quantity: data?.quantity,
      rate: data?.rate,
      gst: data?.gst,
      discount: formik.values.discount === "" ? null : formik.values.discount,
    };
    const isValidPayload =
      payload.gst !== null &&
      payload.rate !== null &&
      payload.quantity !== null;

    if (isValidPayload) {
      setLoader(true);
      gstCountApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const updatedAmount = res?.data;
            setSelectedProducts((prevProducts) =>
              prevProducts.map((product) =>
                product.product_variant_id === data.product_variant_id
                  ? { ...product, gst_amount: updatedAmount }
                  : product
              )
            );
          }
        })
        .catch((err) => {
          setLoader(false);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };
  const calculateServiceGst = (data: any) => {
    const payload = {
      rate: data.rate,
      gst: data.gst,
      discount: formik.values.discount === "" ? null : formik.values.discount,
    };
    const isValidPayload = payload.gst !== null && payload.rate !== null;
    if (isValidPayload) {
      calculateServiceGstApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            setSelectedProducts((prevProducts) =>
              prevProducts.map((product) =>
                product.service_id === data.service_id
                  ? {
                      ...product,
                      gst_amount: res?.data?.gstAmount,
                      amount: res?.data?.totalGstAmount,
                    }
                  : product
              )
            );
          }
        })
        .catch((err) => {
          setLoader(false);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  const productRate = (product: any) => {
    const payload = {
      customer_id: vendorOption?.find(
        (item) => item.id === formik.values.vendorName
      )?.vendor_id,
      product_id: product?.product_id,
      product_variant_id: product?.product_variant_id,
    };
    const isValidPayload =
      payload.customer_id !== null &&
      payload.customer_id !== "" &&
      payload.product_id !== null &&
      payload.product_variant_id !== null;
    if (isValidPayload) {
      setLoader(true);
      productRateApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const updatedAmount = res?.data?.rate;
            setSelectedProducts((prevProducts) =>
              prevProducts?.map((products) =>
                products?.product_id === product
                  ? {
                      ...products,
                      ...((products.rate === null ||
                        products.rate === "" ||
                        product.rate) && { rate: updatedAmount }),
                    }
                  : products
              )
            );
          } else {
          }
        })
        .catch((err) => {})
        .finally(() => {
          setLoader(false);
        });
    }
  };
  const fetchHsnSac = (callback?: (data: any[]) => void) => {
    setLoader(true);
    hsnDropdownApi()
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data?.map((item: any) => ({
            value: item?.code,
            label: item?.code,
            type: item?.type,
            id: item?.id,
          }));
          data.push({
            value: QuotationLabel.AddNewHsn,
            label: QuotationLabel.AddNewHsn,
            id: QuotationLabel.AddNewHsn,
          });
          setHsnSacOption(data);
          if (callback) {
            callback(res.data);
          }
        } else {
          setHsnSacOption([]);
        }
      })
      .catch((err) => {
        setHsnSacOption([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const countTotalAmount = (data: any) => {
    setLoader(true);
    const payload = {
      items: data,
    };
    totalAmountApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setTotalAmount({
            gstAmount: res?.data?.gstAmount?.toFixed(2),
            totalAmount: res?.data?.totalAmount?.toFixed(2),
            totalGstAmount: res?.data?.totalGstAmount?.toFixed(2),
          });
        }
      })
      .catch((err) => {
        setTotalAmount(null);
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const fetchProduct = (callback?: (data: any[]) => void) => {
    setLoader(true);
    const payload = {};
    productListApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const productOptions = res?.data?.map((item: any) => ({
            label: item?.productName,
            id: item?.id,
            value: item?.id,
          }));
          productOptions.push({
            value: QuotationLabel.AddProduct,
            label: QuotationLabel.AddProduct,
            id: QuotationLabel.AddProduct,
          });
          setProductOption(productOptions);
          setProductData(
            res?.data?.map((item: any) => ({
              id: item?.id,
              label: item?.productName,
              value: item?.id,
              product_name: item?.product?.product_code,
              description: item?.product?.product_description,
              product_variant_id: item?.id,
              product_id: item?.product?.id,
              uom: item?.product?.productUom?.id,
              uom_name: item?.product?.productUom?.uom,
            }))
          );

          if (callback) {
            callback(res?.data);
          }
        } else {
          setProductOption([]);
          setProductData([]);
        }
      })
      .catch((err) => {
        setProductOption([]);
        setProductData([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchQuotation = () => {
    if (quotationId) {
      setLoader(true);
      viewQuotationApi(Number(quotationId))
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            setQuotationData(res?.data);
            setSelectedProducts((prevProducts) => {
              const newProducts =
                res?.data?.financeQuotationItem?.map((item: any) => {
                  return {
                    ...item,
                    ...(item?.product_id && {
                      product_name: item?.product?.product_code,
                      isService: false,
                      uom: item?.uom_id,
                      uom_name: item?.product?.productUom?.uom,
                    }),
                    ...(item?.service_id && {
                      service_name: item?.service?.service_name,
                      isService: true,
                    }),
                    amount: item?.with_gst_amount,
                    label: item?.product?.product_code,
                  };
                }) ?? [];
              const amount = {
                totalAmount: res?.data?.total_amount,
                totalGstAmount: res?.data?.with_gst_amount,
                gstAmount: res?.data?.gst_amount,
              };
              setTotalAmount(amount);
              validateTable(
                newProducts,
                productColumns,
                setSelectedProducts,
                setProductValid
              );
              return newProducts;
            });
          } else {
            setQuotationData(null);
          }
        })
        .catch((err) => {
          setQuotationData(null);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  const fetchUom = async () => {
    try {
      const response = await listOfUomForDropdown();
      if (checkStatusCodeSuccess(response?.statusCode)) {
        setUomList(mapToDropdownOptions(response?.data, "id", "uom"));
      } else {
        setUomList(UomValueOptions);
      }
    } catch (err) {
      setUomList(UomValueOptions);
    }
  };

  useEffect(() => {
    Promise.all([
      fetchVendor(),
      fetchHsnSac(),
      fetchProduct(),
      fetchQuotation(),
      fetchService(),
      fetchUom(),
    ]);
  }, []);

  const productColumns: ColDef<any>[] = [
    {
      headerName: QuotationLabel.SrNo,
      headerTooltip: QuotationLabel.SrNo,
      valueGetter: "node.rowIndex + 1",
      cellStyle: { textAlign: "center" },
      editable: false,
      sortable: false,
      filter: false,
      flex: 0.5,
      minWidth: 80,
    },
    {
      headerName: QuotationLabel.ProductName,
      headerTooltip: QuotationLabel.ProductName,
      field: QuotationKey.ProductName,
      tooltipField: QuotationKey.ProductName,
      editable: false,
      flex: 1.5,
      minWidth: 150,
      headerClass: "required",
      cellStyle: (
        params: any
      ): { backgroundColor?: string; cursor?: string } => {
        return !params.data.isService ? {} : { cursor: "not-allowed" };
      },
    },
    {
      headerName: QuotationLabel.ServiceName,
      headerTooltip: QuotationLabel.ServiceName,
      field: QuotationKey.ServiceName,
      tooltipField: QuotationKey.ServiceName,
      editable: false,
      flex: 1.5,
      minWidth: 150,
      headerClass: "required",
      cellStyle: (
        params: any
      ): { backgroundColor?: string; cursor?: string } => {
        return params.data.isService ? {} : { cursor: "not-allowed" };
      },
    },
    {
      headerName: QuotationLabel.DescriptionOfGoods,
      headerTooltip: QuotationLabel.DescriptionOfGoods,
      field: QuotationKey.DescriptionOfGoods,
      tooltipField: QuotationKey.DescriptionOfGoods,
      editable: !viewQuotationMode,
      cellEditor: "agTextCellEditor",
      flex: 1.5,
      minWidth: 150,
      headerClass: "required",
    },
    {
      headerName: QuotationLabel.DescriptionSubHead,
      headerTooltip: QuotationLabel.DescriptionSubHead,
      field: QuotationKey.DescriptionSubHead,
      tooltipField: QuotationKey.DescriptionSubHead,
      editable: (params: any) => !viewQuotationMode && !params.data.isService,
      cellEditor: "agTextCellEditor",
      flex: 1.5,
      minWidth: 150,
      cellStyle: (
        params: any
      ): { backgroundColor?: string; cursor?: string } => {
        return !params.data.isService ? {} : { cursor: "not-allowed" };
      },
    },
    {
      headerName: QuotationLabel.HSN_SAC,
      headerTooltip: QuotationLabel.HSN_SAC,
      field: QuotationKey.HSN_SAC,
      tooltipField: QuotationKey.HSN_SAC,
      editable: !viewQuotationMode,
      cellEditor: "agSelectCellEditor",
      headerClass: "required",
      cellEditorParams: {
        values: hsnSacOption?.map((option) => option?.label),
        formatValue: (value: any) => {
          const selectedOption = hsnSacOption?.find(
            (opt) => opt?.label === value
          );
          return selectedOption ? selectedOption?.label : value;
        },
      },
      valueSetter: (params: any) => {
        const selectedValue = params.newValue;
        const selectedOption = hsnSacOption?.find(
          (opt) => opt?.label === selectedValue
        );
        if (selectedOption) {
          if (selectedOption?.label === QuotationLabel.AddNewHsn) {
            setHsnModal(true);
          } else {
            params.data[QuotationKey.HSN_SAC] = selectedOption?.label;
          }
          return true;
        }
        return false;
      },
      flex: 1.5,
      minWidth: 150,
    },
    {
      headerName: QuotationLabel.Per,
      headerTooltip: QuotationLabel.Per,
      field: QuotationKey.Per,
      tooltipField: QuotationKey.Per,
      editable: (params: any) => !viewQuotationMode && !params.data.isService,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: uomList?.map((option) => option?.label),
      },
      valueGetter: (params: any) => {
        const selectedOption = uomList?.find(
          (opt) => opt?.value === params.data.uom
        );
        return selectedOption ? selectedOption.label : "";
      },
      valueSetter: (params: any) => {
        const selectedOption = uomList?.find(
          (opt) => opt?.label === params.newValue
        );
        if (selectedOption) {
          params.data.uom = selectedOption.value;
          params.data.uom_name = selectedOption.label;
          return true;
        }
        return false;
      },
      flex: 1.5,
      minWidth: 150,
      cellStyle: (
        params: any
      ): { backgroundColor?: string; cursor?: string } => {
        return !params.data.isService ? {} : { cursor: "not-allowed" };
      },
    },
    {
      headerName: QuotationLabel.Quantity,
      headerTooltip: QuotationLabel.Quantity,
      field: QuotationKey.Quantity,
      tooltipField: QuotationKey.Quantity,
      editable: (params: any) => !viewQuotationMode && !params.data.isService,
      valueSetter: (params: any) => {
        const newValue = parseFloat(params.newValue);
        if (!isNaN(newValue) && !params.data.isService) {
          params.data.quantity = newValue;
          countAmount(params.data);
          countGstAmount(params.data);
          setInitialValues(false);
          return true;
        }
        return true;
      },
      flex: 2,
      minWidth: 150,
      cellStyle: (
        params: any
      ): { backgroundColor?: string; cursor?: string } => {
        return !params.data.isService ? {} : { cursor: "not-allowed" };
      },
    },
    {
      headerName: QuotationLabel.Rate,
      headerTooltip: QuotationLabel.Rate,
      field: QuotationKey.Rate,
      tooltipField: QuotationKey.Rate,
      editable: !viewQuotationMode,
      valueSetter: (params: any) => {
        const newValue = parseFloat(params.newValue);
        if (!isNaN(newValue)) {
          params.data.rate = newValue;
          if (params.data.isService) {
            calculateServiceGst(params.data);
          } else {
            countGstAmount(params.data);
            countAmount(params.data);
          }
          setInitialValues(false);
          return true;
        }
        return false;
      },
      flex: 2,
      minWidth: 150,
      headerClass: "required",
    },
    {
      headerName: QuotationLabel.GST,
      headerTooltip: QuotationLabel.GST,
      field: QuotationKey.QuotationGst,
      tooltipField: QuotationKey.QuotationGst,
      editable: (params: any) => !viewQuotationMode,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: TaxType?.map((option) => option?.label),
      },
      valueSetter: (params: any) => {
        const newValue = params.newValue;
        const selectedOption = TaxType?.find((opt) => opt?.label === newValue);
        if (selectedOption) {
          params.data.gst = selectedOption.value;
          if (params.data.isService) {
            calculateServiceGst(params.data);
          } else {
            params.data.gst_amount = countGstAmount(params.data);
            params.amount = countAmount(params.data);
          }
          setInitialValues(false);
          return true;
        }
        return false;
      },
      valueGetter: (params: any) => {
        const selectedOption = TaxType?.find(
          (opt) => opt?.value === params.data.gst
        );
        return selectedOption ? selectedOption.label : "";
      },
      onCellValueChanged: (event: any) => {
        const { newValue, node } = event;
        handleGstChange(newValue, node.rowIndex);
      },
      flex: 2,
      minWidth: 150,
      headerClass: "required",
    },
    {
      headerName: QuotationLabel.GstAmount,
      headerTooltip: QuotationLabel.GstAmount,
      field: QuotationKey.GstAmount,
      tooltipField: QuotationKey.GstAmount,
      editable: false,
      cellRenderer: (params: any) => {
        const amount = params.value?.toFixed(2) || 0;
        return rupeeSymbol(amount);
      },
      flex: 2.5,
      minWidth: 150,
      headerClass: "required",
    },
    {
      headerName: QuotationLabel.Amount,
      headerTooltip: QuotationLabel.Amount,
      field: QuotationKey.Amount,
      tooltipField: QuotationKey.Amount,
      editable: false,
      cellRenderer: (params: any) => {
        const amount = params.value?.toFixed(2) || 0;
        return rupeeSymbol(amount);
      },
      flex: 2.5,
      minWidth: 150,
      headerClass: "required",
    },
  ];

  if (!viewQuotationMode) {
    productColumns.push({
      headerName: ActionLabel,
      headerTooltip: ActionLabel,
      cellRenderer: (params: any) => {
        return (
          <span>
            <i
              className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"
              onClick={() => handleDeleteProduct(params.data)}
            ></i>
          </span>
        );
      },
      cellStyle: { textAlign: "center" },
      editable: false,
      sortable: false,
      filter: false,
      flex: 1.5,
      minWidth: 150,
    });
  }

  const handleDeleteProduct = (product: any) => {
    setSelectedProducts((prevProducts) => {
      const newProduct = prevProducts.filter((item) => item !== product);
      validateTable(
        newProduct,
        productColumns,
        setSelectedProducts,
        setProductValid
      );
      return newProduct;
    });
    if (product?.isService) {
      const deletedService = serviceData?.find((item) => {
        return item?.id === product?.service_id;
      });
      serviceOption?.push(deletedService);
    } else {
      const deletedProduct = productData?.find((item) => {
        return item?.id === product?.product_variant_id;
      });
      productOption?.push(deletedProduct);
    }
    setInitialValues(false);
  };

  const handleGstChange = (newValue: string, rowIndex: number) => {
    if (rowIndex === 0) {
      const selectedGst = TaxType.find((item) => item.label === newValue);
      if (selectedGst) {
        setSelectedProducts((prevData) => {
          return prevData.map((row) => ({
            ...row,
            gst: selectedGst.value,
          }));
        });
        setSelectedGst(selectedGst);
      }
    }
  };

  const formInitialValue = {
    vendorName: quotationId ? quotationData?.auth_id : null,
    vendorId: quotationId ? quotationData?.customer_id : null,
    consignee_address: quotationId ? quotationData?.shipping_address : null,
    buyer_address: quotationId ? quotationData?.billing_address : null,
    quoteNo: quotationId ? quotationData?.quotation_number : null,
    dated: quotationId ? quotationData?.quotation_date : currentDate,
    modeOfPayment: quotationId ? quotationData?.terms_of_payment : null,
    buyerRefNo: quotationId ? quotationData?.buyer_order_no : null,
    buyerRefDate: quotationId
      ? NormalDate(quotationData?.buyer_order_date)
      : currentDate,
    vehicleRegNo: quotationId ? quotationData?.vehicle_no : null,
    chassisNo: quotationId ? quotationData?.chassis_no : null,
    engineNo: quotationId ? quotationData?.engine_no : null,
    destination: quotationId ? quotationData?.destination : null,
    otherRef: quotationId ? quotationData?.other_reference : null,
    warranty: quotationId ? quotationData?.warranty : null,
    termsOfDelivery: quotationId ? quotationData?.terms_of_delivery : null,
    product: null,
    service: null,
    discount:
      quotationId && quotationData?.total_discount !== 0
        ? quotationData?.total_discount
        : null,
    quoteStatus: quotationId ? quotationData?.status : null,
    remarks: quotationId ? quotationData?.remarks : null,
  };
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formInitialValue,
    validationSchema,
    onSubmit: (values) => {
      let isValid = true;
      if (selectedProducts?.length > 0) {
        selectedProducts?.forEach((row, index) => {
          if (
            !row.description ||
            !row.hsn_sac_code ||
            !row.gst ||
            !row.rate ||
            !row.amount
          ) {
            isValid = false;
            setProductValid(false);
          }
        });
      } else {
        isValid = false;
        setProductValid(false);
      }
      if (isValid) {
        handleFormSubmit(values);
      }
    },
  });

  useMemo(() => {
    if (!initialValues) {
      selectedProducts?.map((item) => {
        countAmount(item);
        countGstAmount(item);
      });
    }
  }, [formik.values.discount]);

  const handleFormSubmit = (values: any) => {
    validateTable(
      selectedProducts,
      productColumns,
      setSelectedProducts,
      setProductValid
    );
    const quotationProduct = selectedProducts?.map((item) => {
      return {
        ...(item?.id && { id: item?.id }),
        ...(!item?.isService && {
          product_id: item?.product_id,
          product_variant_id: item?.product_variant_id,
        }),
        ...(item?.isService && {
          service_id: item?.service_id,
        }),
        description: item?.description,
        sub_description: item?.sub_description,
        hsn_sac_code: item?.hsn_sac_code,
        gst: item?.gst,
        quantity: item?.quantity,
        rate: item?.rate,
        amount: Number(item?.amount) - Number(item?.gst_amount),
        discount:
          values?.discount !== "" && values?.discount !== null
            ? Number(values?.discount)
            : null,
        uom_id: item?.uom,
        gst_amount: Number(item?.gst_amount),
        with_gst_amount: item?.amount,
      };
    });
    const payload = {
      ...(quotationId && { id: Number(quotationId) }),
      auth_id: vendorId?.id,
      customer_name: vendorId?.name,
      customer_id: values.vendorId,
      shipping_address: values.consignee_address,
      billing_address: values.buyer_address,
      terms_of_payment: values.modeOfPayment,
      buyer_order_no: values.buyerRefNo,
      buyer_order_date: values.buyerRefDate,
      vehicle_no: values.vehicleRegNo,
      chassis_no: values.chassisNo,
      quotation_date: values.dated,
      engine_no: values.engineNo,
      destination: values.destination,
      other_reference: values.otherRef,
      warranty: values.warranty,
      terms_of_delivery: values.termsOfDelivery,
      financeQuotationItem: quotationProduct,
      discount:
        values?.discount !== "" && values?.discount !== null
          ? Number(values?.discount)
          : null,
      total_amount: Number(totalAmount?.totalAmount),
      with_gst_amount: Number(totalAmount?.totalGstAmount),
      gst_amount: Number(totalAmount?.gstAmount),
    };
    setSubmitLoader(true);
    quotationApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          toast.success(res?.message);
          navigate("/quotation");
        } else {
          if (Array.isArray(res?.message)) {
            res?.message?.map((message: string) => {
              toast.error(message);
            });
          } else {
            toast.error(res?.response?.message || res?.message);
          }
        }
      })
      .catch((err) => {
        errorHandler(err);
      })
      .finally(() => {
        setSubmitLoader(false);
      });
  };
  useEffect(() => {
    const selectedProductData = productData?.find(
      (item) => item?.id === Number(formik.values.product)
    );
    if (selectedProductData) {
      const newProduct = {
        ...selectedProductData,
        product_id: selectedProductData?.product_id,
        product_name: selectedProductData?.product_name,
        product_variant_id: selectedProductData?.product_variant_id,
        id: null,
        discount: formik.values.discount || 0,
        rate: null,
        gst: null,
        quantity: null,
        gst_amount: null,
        amount: null,
        hsn_sac_code: null,
        isService: false,
        uom: selectedProductData?.uom,
        uom_name: selectedProductData?.uom_name,
      };
      setSelectedProducts((prev) => [...prev, newProduct]);
      formik.setFieldValue("product", null);
    } else if (formik.values.product === QuotationLabel.AddProduct) {
      setProductModal(true);
      formik.setFieldValue("product", null);
    }
  }, [formik.values.product, productData]);

  useEffect(() => {
    if (formik.values.service === QuotationLabel.AddService) {
      setServiceModal(true);
      formik.setFieldValue("service", null);
    }
    const selectedServiceData = serviceOption?.find(
      (item) => item?.id === Number(formik.values.service)
    );
    if (selectedServiceData) {
      const newProduct = {
        ...selectedServiceData,
        service_id: selectedServiceData?.id,
        srevice_name: selectedServiceData?.service_name,
        id: null,
        discount: formik.values.discount ?? 0,
        rate: selectedServiceData?.cost,
        gst: selectedServiceData?.gst,
        quantity: null,
        gst_amount: selectedServiceData?.gstAmount,
        amount: selectedServiceData?.withGstAmount,
        hsn_sac_code: selectedServiceData?.hsnSacCode,
        isService: true,
      };
      setSelectedProducts((prev) => [...prev, newProduct]);
      formik.setFieldValue("service", null);
    }
  }, [formik.values.service, serviceOption]);

  useEffect(() => {
    if (formik.values.vendorName === QuotationLabel.AddCustomer) {
      setCustomerModal(true);
    }
  }, [formik.values.vendorName]);

  useEffect(() => {
    if (!initialValues) {
      selectedProducts?.map((item) => {
        productRate(item);
      });
    }
  }, [formik.values.vendorName, selectedProducts?.length]);

  useEffect(() => {
    const payload = selectedProducts?.map((item) => {
      return {
        gst_amount: item?.gst_amount,
        amount: item?.amount,
      };
    });
    if (payload && !initialValues) {
      countTotalAmount(payload);
    }
  }, [
    selectedProducts?.length,
    JSON.stringify(
      selectedProducts?.map((item) => ({
        amount: item?.amount,
        gst_amount: item?.gst_amount,
      }))
    ),
  ]);

  useEffect(() => {
    if (!initialValues) {
      selectedProducts?.map((item) => {
        countAmount(item);
        countGstAmount(item);
      });
    }
  }, [
    JSON.stringify(
      selectedProducts?.map((item) => ({
        gst: item?.gst,
      }))
    ),
  ]);
  useEffect(() => {
    if (!initialValues) {
      const gstVal = TaxType.find(
        (item) => item.value === selectedProducts[0]?.gst
      );

      handleGstChange(String(gstVal?.label), 0);
    }
  }, [selectedProducts?.length]);

  useEffect(() => {
    const allProductIds = selectedProducts?.map(
      (item) => item.product_variant_id
    );
    setProductOption((prevOptions) =>
      prevOptions.filter((product) => !allProductIds.includes(product.value))
    );
    const allServiceIds = selectedProducts?.map((item) => item?.service_id);
    setServiceOption((prevOptions) =>
      prevOptions.filter((item) => !allServiceIds.includes(item.value))
    );
  }, [selectedProducts]);

  useEffect(() => {
    const selectedVendors = vendorData?.find((item: any) => {
      return Number(item?.id) === Number(formik.values.vendorName);
    });
    setVendorId(selectedVendors);
    const address = selectedVendors?.address;
    const consigneeAddress = address?.shipping_address_1;
    const buyerAddress = address?.billing_address_1;
    setSelectedVendorData({
      vendorId: selectedVendors?.user_draft_id ?? null,
      ...(!vendor && {
        consignee_address: consigneeAddress ?? null,
        buyer_address: buyerAddress ?? null,
      }),
    });
  }, [formik.values.vendorName, vendorData]);

  useEffect(() => {
    if (selectedVendorData) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        ...selectedVendorData,
      }));
    } else {
      formik.setValues((prevValues) => ({
        ...prevValues,
        vendorId: "",
        consignee_address: "",
        buyer_address: "",
      }));
    }
  }, [selectedVendorData]);

  const handleVendorChange = (value: any) => {
    const selectedVendorData = vendorData?.find((item: any) => {
      return Number(item?.id) === Number(value);
    });
    const address = selectedVendorData?.address;
    const consigneeAddress = address?.shipping_address_1;
    const buyerAddress = address?.billing_address_1;
    formik.setFieldValue("consignee_address", consigneeAddress);
    formik.setFieldValue("buyer_address", buyerAddress);
  };

  const hsnForm = useFormik({
    initialValues: {
      code: "",
      type: 1,
      hsnId: null,
    },
    validationSchema: yup.object({
      code: yup.string().required(validationMessages.required(Code)),
      type: yup
        .number()
        .required(validationMessages.required(Type))
        .positive()
        .integer(),
    }),
    onSubmit: async (values) => {
      setModalLoader(true);
      const typeString = values.type === 1 ? Hsn : Sac;
      const payload = {
        code: values.code,
        type: typeString,
        hsnId: values.hsnId,
      };
      createHSN(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            setHsnModal(false);
            fetchHsnSac((hsnData) => {
              const newHsnSac = hsnData?.find(
                (item: any) =>
                  item?.code === values.code && item?.type === typeString
              );

              if (newHsnSac) {
                const currentRow = gridRef.current?.api.getFocusedCell();
                if (currentRow) {
                  const rowNode = gridRef.current?.api.getDisplayedRowAtIndex(
                    currentRow.rowIndex
                  );
                  if (rowNode) {
                    setSelectedProducts((prevProducts) =>
                      prevProducts.map((product, index) =>
                        index === currentRow.rowIndex
                          ? {
                              ...product,
                              hsn_sac_code: newHsnSac.code,
                              hsn_sac_id: newHsnSac.id,
                            }
                          : product
                      )
                    );
                  }
                }
              }
            });
            hsnForm.resetForm();
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message || error?.message);
        })
        .finally(() => setModalLoader(false));
    },
  });

  const handleDownload = () => {
    pdfDownload(
      quotationPdfApi,
      quotationData?.id,
      `quotation_${quotationData?.quotation_number}.pdf`,
      setPdfLoader
    );
  };
  const handleProductSuccess = (selectedProduct?: any) => {
    fetchProduct((productData) => {
      if (selectedProduct) {
        const newProduct = productData.find(
          (item: any) => item?.product?.id === selectedProduct?.id
        );
        if (newProduct) {
          formik.setFieldValue("product", newProduct.id);
        }
      }
    });
  };
  const handleCustomerSuccess = (selectedCustomer?: any) => {
    fetchVendor((vendorData) => {
      if (selectedCustomer) {
        const newCustomer = vendorData.find(
          (item: any) => item.id === selectedCustomer?.id
        );
        if (newCustomer) {
          formik.setFieldValue("vendorName", newCustomer.id);
        }
      }
    });
  };

  const handleServiceSuccess = (selectedService: any) => {
    fetchService((serviceData: any) => {
      if (selectedService) {
        const newService = serviceData.services.find(
          (item: any) => item.id === selectedService.id
        );
        if (newService) {
          const serviceToAdd = {
            service_id: newService.id,
            service_name: newService.service_name,
            description: newService.description,
            hsn_sac_code: newService.hsnSac?.code,
            gst: newService.gst,
            rate: newService.cost,
            gst_amount: newService.gst_amount,
            amount: newService.with_gst_amount,
            isService: true,
          };
          setSelectedProducts((prev) => [...prev, serviceToAdd]);
        }
      }
    });
  };

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">
            {quotationId
              ? viewQuotationMode
                ? `${QuotationLabel.ViewQuotation}-${
                    quotationData?.quotation_number || ""
                  }`
                : `${QuotationLabel.EditQuotation}-${
                    quotationData?.quotation_number || ""
                  }`
              : QuotationLabel.NewQuotation}
          </span>
          <div className="d-flex align-items-center gap-2">
            {viewQuotationMode && (
              <span className="tooltip-container">
                <BaseButton
                  className="btn btn-info"
                  loader={pdfLoader}
                  onClick={() => handleDownload()}
                  children={
                    <>
                      <i className="mdi mdi-download-outline" />
                    </>
                  }
                />
                <span className="tooltip-text">{DownloadLabel}</span>
              </span>
            )}
            <span>
              <BaseButton
                className="btn btn-danger"
                onClick={() => navigate("/quotation")}
                children={
                  <>
                    <i className="mdi mdi-arrow-left" />
                  </>
                }
              />
            </span>
          </div>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <FormFields
              formik={formik}
              vendorOption={vendorOption}
              productOption={productOption}
              serviceOption={serviceOption}
              handleVendorChange={handleVendorChange}
              viewQuotationMode={viewQuotationMode}
              quotationId={quotationId}
              quotationData={quotationData}
              setSelectedVendorData={setSelectedVendorData}
              setInitialValue={setInitialValues}
              selectedProducts={selectedProducts}
            />
            <div className="table-css">
              {!productValid && selectedProducts.length > 0 && (
                <span className="text-danger">{TableErrorMessage}</span>
              )}
              <div className="ag-theme-quartz">
                <AgGridReact
                  ref={gridRef}
                  rowData={selectedProducts}
                  columnDefs={productColumns}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                    tooltipComponentParams: {
                      color: "blue",
                    },
                  }}
                  domLayout="normal"
                  getRowHeight={() => 35}
                  overlayNoRowsTemplate={notFound.dataNotFound}
                />
              </div>
            </div>
            <div className="d-flex justify-content-end align-items-start">
              <div className="mt-4 d-flex gap-4">
                <div className="d-flex flex-column text-start">
                  <h6 className="mb-1 text-dark">
                    {QuotationLabel.TotalAmount}
                  </h6>
                  <span className="text-center">
                    {totalAmount?.totalAmount
                      ? rupeeSymbol(totalAmount?.totalAmount)
                      : notFound.nullData}
                  </span>
                </div>
                <div className="d-flex flex-column text-start">
                  <h6 className="mb-1 font-bold text-dark">
                    {QuotationLabel.TotalGSTAmount}
                  </h6>
                  <span className="text-center">
                    {totalAmount?.totalGstAmount
                      ? rupeeSymbol(totalAmount?.totalGstAmount)
                      : notFound.nullData}
                  </span>
                </div>
              </div>
            </div>
            {!viewQuotationMode && (
              <div className="row mt-3 d-flex justify-content-end">
                <div className="col-auto d-flex justify-content-end gap-2 text-end">
                  <BaseButton
                    type="submit"
                    label={quotationId ? updateLabel : submitLabel}
                    color="success"
                    loader={submitLoader}
                    disable={submitLoader}
                    onClick={() => formik.handleSubmit()}
                  />
                  <BaseButton
                    type="button"
                    label={cancelLabel}
                    onClick={() => navigate("/quotation")}
                    color="danger"
                  />
                </div>
              </div>
            )}
          </div>
          <BaseModal
            open={hsnModal}
            toggle={() => {
              setHsnModal(!hsnModal);
              hsnForm.resetForm();
            }}
            submit={() => hsnForm.handleSubmit()}
            title={addHSNLabel}
            closeLabel={NoLabel}
            submitLabel={YesLabel}
            closeLabelColor="danger"
            submitLabelColor="success"
            loader={modalLoader}
            size="sm"
          >
            {modalLoader ? (
              <Loader />
            ) : (
              <div className="form-group">
                <BaseInput
                  type="text"
                  label={Code}
                  placeholder={InputPlaceHolder(Code)}
                  name="code"
                  className="form-control"
                  value={hsnForm.values.code}
                  onChange={hsnForm.handleChange}
                  handleBlur={hsnForm.handleBlur}
                  touched={hsnForm.touched.code}
                  error={hsnForm.errors.code}
                  required
                />

                <div className="mt-2">
                  <BaseSelect
                    name="type"
                    label={Type}
                    options={typeList}
                    value={hsnForm.values.type}
                    placeholder={SelectPlaceHolder(Type)}
                    handleChange={hsnForm.setFieldValue}
                    handleBlur={hsnForm.handleBlur}
                    error={hsnForm.errors.type}
                    touched={hsnForm.touched.type}
                    required
                  />
                </div>
              </div>
            )}
          </BaseModal>
          <ProductModal
            open={productModal}
            toggle={() => setProductModal(!productModal)}
            onSuccess={handleProductSuccess}
          />
          <CustomerModal
            open={customerModal}
            toggle={() => {
              setCustomerModal(!customerModal);
              formik.setFieldValue(
                "vendorName",
                quotationId ? quotationData?.auth_id : null
              );
            }}
            onSuccess={handleCustomerSuccess}
          />
          <ServiceModal
            open={serviceModal}
            toggle={() => setServiceModal(!serviceModal)}
            onSuccess={handleServiceSuccess}
          />
        </div>
      </div>
    </div>
  );
};

export default QuotationForm;
